import Dexie, { Table } from 'dexie'
import store from '@/store'

export interface ExamInfo {
    id?: number
    remoteId?: string
    subjectId?: string
    subject: string
    createTime?: number
    userId?: string
    exameMode: string
    questionType: string
    questionCount: number
    questionCategory: string
    timer?: number
    mode?: string
    images?: ImageInfo[]
    taskList: string[]
    testList?: QuestionInfo[]
    costTime?: number
    status?: string
}

export interface ImageInfo {
    id: string
    taskId: string
    originName: string
    url: string
}

export interface QuestionInfo {
    type: string
    id: number
    tag: string[]
    title: string
    rightKey: any[]
    list: AnswerInfo[]
    skill: string
    detail: string
    collect?: boolean
    answer?: number
    answerTime?: number
    subject?: string
    examId?: number
    extExam?: ExamInfo
}

export interface AnswerInfo {
    id: number
    option: string
    text: string
    key: number
    isRight: boolean
}

// 创建历史记录时使用的类型（不包含自增id）
export type CreateTestPrepHistory = Omit<ExamInfo, 'id'>

// 数据库类
export class TestPrepDatabase extends Dexie {
    // 声明表
    testPrepHistory!: Table<CreateTestPrepHistory, number>

    constructor() {
        super('TestPrepDatabase')

        // 定义数据库结构
        this.version(1).stores({
            // 历史记录表：按id、userId和创建时间索引
            testPrepHistory: '++id, userId, createTime, title'
        })

        // 添加钩子函数
        // @ts-ignore
        this.testPrepHistory.hook('creating', (primKey: any, obj: any, trans: any) => {
            obj.createTime = obj.createTime || Date.now()
            obj.userId = store.getters['user/userId']
            obj.costTime = 0
            obj.status = 'pending'
        })

        // 暂时保留
        // this.testPrepHistory.hook('updating', (modifications: any, primKey: any, obj: any, trans: any) => {
        // })
    }

    // 初始化数据库
    async initDatabase() {
        try {
            await this.open()

            return true
        } catch (error) {
            console.error('Init database failed:', error)
            return false
        }
    }
}

// 导出数据库实例
export const testPrepDB = new TestPrepDatabase()

// 数据库工具函数
export const dbUtils = {
    // 创建新的历史记录
    async createExam(data: ExamInfo) {
        // 使用 JSON 序列化来清理 Vue 响应式对象，确保数据可以被 IndexedDB 存储
        const cleanedData = JSON.parse(JSON.stringify(data))

        const id = await testPrepDB.testPrepHistory.add(cleanedData)
        // @ts-ignore
        return { id, ...cleanedData }
    },

    async getExamDetail(examId: number) {
        if (!examId) {
            return null
        }

        const exam = await testPrepDB.testPrepHistory.get(examId)

        return exam || null
    },

    async updateQuestionCollect(data: { examId: number; questionId: number; collect: boolean }) {
        const exam = await testPrepDB.testPrepHistory.get(data.examId)

        if (exam) {
            exam.testList =
                exam.testList?.map((item: QuestionInfo) => {
                    if (item.id === data.questionId) {
                        item.collect = data.collect
                    }

                    return item
                }) || []
            return await testPrepDB.testPrepHistory.put(exam)
        } else {
            return null
        }
    },

    async updateQuestionOption(data: { examId: number; questionId: number; answer: number }) {
        const exam = await testPrepDB.testPrepHistory.get(data.examId)

        if (exam) {
            exam.testList =
                exam.testList?.map((item: QuestionInfo) => {
                    if (item.id === data.questionId) {
                        if (!item.answer) {
                            item.answerTime = Date.now()
                        }
                        item.answer = data.answer
                    }

                    return item
                }) || []
            return await testPrepDB.testPrepHistory.put(exam)
        } else {
            return null
        }
    },

    async updateExamStatus(data: { examId: number; status: string }) {
        const exam = await testPrepDB.testPrepHistory.get(data.examId)

        if (exam) {
            exam.status = data.status
            return await testPrepDB.testPrepHistory.put(exam)
        } else {
            return null
        }
    },

    async updateExamCostTime(data: { examId: number; costTime: number }) {
        const exam = await testPrepDB.testPrepHistory.get(data.examId)

        if (exam) {
            exam.costTime = (exam.costTime || 0) + data.costTime
            return await testPrepDB.testPrepHistory.put(exam)
        } else {
            return null
        }
    },

    async getExamListForHomeData() {
        return await testPrepDB.testPrepHistory.where('userId').equals(store.getters['user/userId']).toArray()
    },

    // 获取历史记录列表
    async getTestPrepHistoryList(pageInfo: any) {
        const { currentPage = 1, pageSize = 9 } = pageInfo
        const offset = (currentPage - 1) * pageSize

        // 获取所有用户数据并过滤出包含send消息的记录
        const allUserData = await testPrepDB.testPrepHistory.where('userId').equals(store.getters['user/userId']).toArray()

        // 按创建时间倒序排列
        const sortedData = allUserData.sort((a, b) => (b.createTime || 0) - (a.createTime || 0))

        // 获取过滤后的总数
        const total = sortedData.length

        // 手动分页
        const result = sortedData.slice(offset, offset + pageSize)

        return {
            data: result,
            total,
            currentPage,
            pageSize,
            totalPages: Math.ceil(total / pageSize)
        }
    },

    async getTestPrepHistorySubjectList(pageInfo: any) {
        const { currentPage = 1, pageSize = 9 } = pageInfo
        const offset = (currentPage - 1) * pageSize

        // 获取所有用户数据并过滤出包含send消息的记录
        const allUserData = await testPrepDB.testPrepHistory.where('userId').equals(store.getters['user/userId']).toArray()

        const res: any = {}

        allUserData.forEach((item: ExamInfo) => {
            if (item.subjectId) {
                if (res[item.subjectId]) {
                    res[item.subjectId].push(item)
                } else {
                    res[item.subjectId] = [item]
                }
            }
        })

        // 获取过滤后的总数
        const total = Object.keys(res).length

        // 手动分页
        const result: any = Object.keys(res)
            .slice(offset, offset + pageSize)
            .map((item: any) => {
                return {
                    subjectId: item,
                    subject: res[item][0].subject,
                    count: res[item].length
                }
            })

        return {
            data: result,
            total,
            currentPage,
            pageSize,
            totalPages: Math.ceil(total / pageSize)
        }
    },

    async getTestPrepHistoryListBySubjectId(data: { pageInfo: any; subjectId: string }) {
        const { currentPage = 1, pageSize = 9 } = data.pageInfo
        const offset = (currentPage - 1) * pageSize

        // 获取所有用户数据并过滤出包含send消息的记录
        const allUserData = await testPrepDB.testPrepHistory
            .where('userId')
            .equals(store.getters['user/userId'])
            .and((item: ExamInfo) => item.subjectId === data.subjectId)
            .toArray()

        // 按创建时间倒序排列
        const sortedData = allUserData.sort((a: ExamInfo, b: ExamInfo) => (b.createTime || 0) - (a.createTime || 0))

        // 获取过滤后的总数
        const total = sortedData.length

        // 手动分页
        const result = sortedData.slice(offset, offset + pageSize)

        return {
            data: result,
            total,
            currentPage,
            pageSize,
            totalPages: Math.ceil(total / pageSize)
        }
    },

    async getTestPrepFavorateList(data: { pageInfo: any; subjectId: string }) {
        const { currentPage = 1, pageSize = 9 } = data.pageInfo
        const offset = (currentPage - 1) * pageSize

        // 获取所有用户数据并过滤出包含send消息的记录
        const allUserData = await testPrepDB.testPrepHistory
            .where('userId')
            .equals(store.getters['user/userId'])
            .and((item: ExamInfo) => item.subjectId === data.subjectId)
            .toArray()

        // 按创建时间倒序排列
        const sortedData = allUserData.sort((a: ExamInfo, b: ExamInfo) => (b.createTime || 0) - (a.createTime || 0))

        let res: any[] = []

        sortedData.forEach((item: ExamInfo) => {
            const collect = item.testList?.filter((test: QuestionInfo) => test.collect)

            res = res.concat(
                collect?.map(sItem => {
                    sItem.subject = item.subject
                    sItem.examId = item.id
                    sItem.extExam = item
                    return sItem
                })
            )
        })

        // 获取过滤后的总数
        const total = res.length

        // 手动分页
        const result = res.slice(offset, offset + pageSize)

        return {
            data: result,
            total,
            currentPage,
            pageSize,
            totalPages: Math.ceil(total / pageSize)
        }
    }
}
